const DRUM_VOICES = 6;
const SYNTH_VOICES = 4;
const STEP_COUNT = 64;
const PITCH_CLASS_COUNT = 12;
const RETRY_DELAY_MS = 3000;
const BASE_NOTE = 36;
const BASE_OCTAVE = Math.floor(BASE_NOTE / PITCH_CLASS_COUNT);

const SYSEX_START = 0xf0;
const SYSEX_END = 0xf7;
const MANUFACTURER_ID = [0x7d, 0x42, 0x42];
const MSG_PATTERN_SNAPSHOT = 0x01;
const MSG_PATTERN_REQUEST = 0x02;
const MSG_STEP_POSITIONS = 0x03;
const MSG_SET_DRUM_STEP = 0x04;
const MSG_SET_SYNTH_STEP = 0x05;
const DRUM_LABELS = ["BD", "SD", "TOM", "CH", "OH", "CYM"];
const OFFSET_BIAS = 8;
const MAX_RAW_NOTE = 63;

const state = {
  pages: new Uint8Array(DRUM_VOICES + SYNTH_VOICES),
  drumColumns: new Uint8Array(STEP_COUNT),
  synthMask: new Uint8Array(STEP_COUNT),
  synthNotes: Array.from({ length: SYNTH_VOICES }, () => new Uint8Array(STEP_COUNT)),
  synthOffsets: new Int8Array(SYNTH_VOICES),
  currentSteps: new Uint8Array(DRUM_VOICES + SYNTH_VOICES),
  selectedVoice: 0,
  selectedPage: 0,
};

const refs = {
  status: document.getElementById("status-indicator"),
  drumGrid: document.getElementById("drum-grid"),
  synthWrapper: document.getElementById("synth-wrapper"),
};

const templates = {
  drumCell: document.getElementById("drum-cell-template"),
  synthTrack: document.getElementById("synth-track-template"),
  synthCell: document.getElementById("synth-cell-template"),
};

const drumCells = Array.from({ length: DRUM_VOICES }, () => new Array(STEP_COUNT));
const synthCells = Array.from({ length: SYNTH_VOICES }, () =>
  new Array(STEP_COUNT)
);
const synthTracks = new Array(SYNTH_VOICES);
const synthLastNotes = new Uint8Array(SYNTH_VOICES);

const paintState = {
  active: false,
  target: null,
  mode: null,
  pointerId: null,
  buttonMask: 0,
  synthNote: 0,
  startVoice: null,
  startStep: null,
  startValue: null,
  startNote: null,
  lastStep: null,
  appliedStart: false,
  visited: new Set(),
  initialMode: null,
};

const BUTTON_PRIMARY = 1;

let midiAccess = null;
let activeInput = null;
let activeOutput = null;
let retryTimer = null;
let hasRequestedSnapshot = false;

const midiConnectionListeners = new Set();
const midiMessageListeners = new Set();

function notifyMidiConnectionChanged() {
  const snapshot = { input: activeInput, output: activeOutput };
  midiConnectionListeners.forEach((listener) => {
    try {
      listener(snapshot);
    } catch (error) {
      console.error("Error in MIDI connection listener:", error);
    }
  });
}

function notifyMidiMessage(event) {
  midiMessageListeners.forEach((listener) => {
    try {
      listener(event);
    } catch (error) {
      console.error("Error in MIDI message listener:", error);
    }
  });
}

function ensureMidiBridge() {
  const bridge = {
    __isBlastBeatsBridge: true,
    getCurrentConnection() {
      return { input: activeInput, output: activeOutput };
    },
    onConnectionChange(listener) {
      if (typeof listener !== "function") {
        return () => {};
      }
      midiConnectionListeners.add(listener);
      try {
        listener({ input: activeInput, output: activeOutput });
      } catch (error) {
        console.error("Error in MIDI connection listener:", error);
      }
      return () => midiConnectionListeners.delete(listener);
    },
    onMessage(listener) {
      if (typeof listener !== "function") {
        return () => {};
      }
      midiMessageListeners.add(listener);
      return () => midiMessageListeners.delete(listener);
    },
    send(data, timestamp) {
      if (!activeOutput) {
        throw new Error("No active MIDI output available.");
      }
      activeOutput.send(data, timestamp);
    },
  };

  if (!window.blastBeatsMidi || !window.blastBeatsMidi.__isBlastBeatsBridge) {
    window.blastBeatsMidi = bridge;
    window.dispatchEvent(
      new CustomEvent("blastbeats-midi-ready", { detail: bridge })
    );
  } else {
    window.blastBeatsMidi.getCurrentConnection = bridge.getCurrentConnection;
    window.blastBeatsMidi.onConnectionChange = bridge.onConnectionChange;
    window.blastBeatsMidi.onMessage = bridge.onMessage;
    window.blastBeatsMidi.send = bridge.send;
  }
}

ensureMidiBridge();
notifyMidiConnectionChanged();

function init() {
  buildDrumGrid();
  buildSynthTracks();
  window.addEventListener("pointerup", handleGlobalPointerUp);
  window.addEventListener("pointercancel", handleGlobalPointerUp, { capture: true });
  window.addEventListener("blur", handleGlobalBlur);
  updateStatus("Searching for BlastBeats MIDI…");
  requestAccess();
}

function buildDrumGrid() {
  for (let voice = 0; voice < DRUM_VOICES; voice++) {
    const label = document.createElement("div");
    label.className = "drum-label";
    const voiceLabel = DRUM_LABELS[voice] || `V${voice + 1}`;
    label.textContent = voiceLabel;
    refs.drumGrid.appendChild(label);

    for (let step = 0; step < STEP_COUNT; step++) {
      const cell = templates.drumCell.content.firstElementChild.cloneNode(true);
      cell.dataset.voice = voice;
      cell.dataset.step = step;
      cell.setAttribute("role", "button");
      cell.setAttribute("aria-pressed", "false");
      cell.setAttribute("aria-label", `${voiceLabel} step ${step + 1}`);
      cell.tabIndex = 0;
      cell.addEventListener("pointerdown", handleDrumCellPointerDown);
      cell.addEventListener("pointerenter", handleDrumCellPointerEnter);
      cell.addEventListener("pointerup", handleDrumCellPointerUp);
      cell.addEventListener("contextmenu", preventContextMenu);
      cell.addEventListener("keydown", handleDrumCellKeyDown);
      if (step % 16 === 0) {
        cell.classList.add("step-marker-16");
      } else if (step % 8 === 0) {
        cell.classList.add("step-marker-8");
      }
      const flash = cell.querySelector(".step-flash");
      if (flash) {
        flash.addEventListener("animationend", () => {
          cell.classList.remove("is-current-step");
        });
      }
      refs.drumGrid.appendChild(cell);
      drumCells[voice][step] = cell;
    }
  }
}

function buildSynthTracks() {
  for (let voice = 0; voice < SYNTH_VOICES; voice++) {
    const trackFragment = templates.synthTrack.content.cloneNode(true);
    const track = trackFragment.querySelector(".synth-track");
    const grid = track.querySelector(".synth-grid");
    const label = track.querySelector(".track-label");

    label.textContent = `Synth ${voice + 1}`;
    synthTracks[voice] = track;

    for (let step = 0; step < STEP_COUNT; step++) {
      const cellFragment = templates.synthCell.content.cloneNode(true);
      const cell = cellFragment.querySelector(".synth-cell");
      const noteIndicator = cell.querySelector(".note-indicator");
      cell.dataset.voice = voice;
      cell.dataset.step = step;
      if (step % 16 === 0) {
        cell.classList.add("step-marker-16");
      } else if (step % 8 === 0) {
        cell.classList.add("step-marker-8");
      }
      const flash = cell.querySelector(".step-flash");
      if (flash) {
        flash.addEventListener("animationend", () => {
          cell.classList.remove("is-current-step");
        });
      }
      cell.addEventListener("pointerdown", handleSynthCellPointerDown);
      cell.addEventListener("pointerenter", handleSynthCellPointerEnter);
      cell.addEventListener("pointermove", handleSynthCellPointerMove);
      cell.addEventListener("pointerup", handleSynthCellPointerUp);
      cell.addEventListener("contextmenu", preventContextMenu);
      grid.appendChild(cell);

      synthCells[voice][step] = { cell, noteIndicator };
    }

    refs.synthWrapper.appendChild(trackFragment);
  }
}

function preventContextMenu(event) {
  event.preventDefault();
}

function startPaint(target, mode, pointerId, buttonMask) {
  if (paintState.active) {
    endPaint();
  }
  paintState.visited.clear();
  paintState.active = true;
  paintState.target = target;
  paintState.mode = mode;
  paintState.pointerId = pointerId;
  paintState.buttonMask = buttonMask;
  paintState.initialMode = null;
}

function endPaint() {
  paintState.active = false;
  paintState.target = null;
  paintState.mode = null;
  paintState.pointerId = null;
  paintState.buttonMask = 0;
  paintState.synthNote = 0;
  paintState.startVoice = null;
  paintState.startStep = null;
  paintState.startValue = null;
  paintState.startNote = null;
  paintState.lastStep = null;
  paintState.appliedStart = false;
  paintState.visited.clear();
  paintState.initialMode = null;
}

function handleGlobalPointerUp(event) {
  if (!paintState.active) {
    return;
  }
  if (event && paintState.pointerId !== null && event.pointerId !== paintState.pointerId) {
    return;
  }
  endPaint();
}

function handleGlobalBlur() {
  endPaint();
}

function handleDrumCellPointerDown(event) {
  if (event.button !== 0) {
    return;
  }
  if (event.cancelable) {
    event.preventDefault();
  }
  const cell = event.currentTarget;
  if (!cell) {
    return;
  }
  const voice = Number(cell.dataset.voice);
  const step = Number(cell.dataset.step);
  const bit = 1 << voice;
  const isActive = Boolean(state.drumColumns[step] & bit);
  startPaint("drum", null, event.pointerId ?? null, BUTTON_PRIMARY);
  paintState.startVoice = voice;
  paintState.startStep = step;
  paintState.startValue = isActive;
  paintState.lastStep = step;
  paintState.appliedStart = false;
  paintState.initialMode = isActive ? "erase" : "fill";
}

function handleDrumCellKeyDown(event) {
  if (event.repeat) {
    return;
  }
  if (event.code !== "Space" && event.code !== "Enter") {
    return;
  }
  if (event.cancelable) {
    event.preventDefault();
  }
  const cell = event.currentTarget;
  if (!cell) {
    return;
  }
  const voice = Number(cell.dataset.voice);
  const step = Number(cell.dataset.step);
  toggleDrumStep(voice, step);
}

function handleDrumCellPointerEnter(event) {
  if (!paintState.active || paintState.target !== "drum") {
    return;
  }
  if (paintState.pointerId !== null && event.pointerId !== paintState.pointerId) {
    return;
  }
  if ((event.buttons & paintState.buttonMask) === 0) {
    endPaint();
    return;
  }
  const cell = event.currentTarget;
  const voice = Number(cell.dataset.voice);
  const step = Number(cell.dataset.step);
  updateDrumPaintMode(step);
  if (paintState.mode === null) {
    return;
  }
  if (!paintState.appliedStart) {
    const startKey = `${paintState.startVoice}:${paintState.startStep}`;
    applyDrumPaint(paintState.startVoice, paintState.startStep, paintState.mode);
    paintState.visited.add(startKey);
    paintState.appliedStart = true;
    paintState.lastStep = paintState.startStep;
  }
  const key = `${voice}:${step}`;
  if (paintState.visited.has(key)) {
    return;
  }
  paintState.visited.add(key);
  paintState.lastStep = step;
  applyDrumPaint(voice, step, paintState.mode);
}

function handleDrumCellPointerUp(event) {
  if (paintState.target !== "drum") {
    return;
  }
  if (paintState.pointerId !== null && event.pointerId !== paintState.pointerId) {
    return;
  }
  if (paintState.mode === null) {
    const cell = event.currentTarget;
    const voice = Number(cell.dataset.voice);
    const step = Number(cell.dataset.step);
    toggleDrumStep(voice, step);
  }
  endPaint();
}

function toggleDrumStep(voice, step) {
  if (!Number.isInteger(voice) || voice < 0 || voice >= DRUM_VOICES) {
    return;
  }
  if (!Number.isInteger(step) || step < 0 || step >= STEP_COUNT) {
    return;
  }

  const bit = 1 << voice;
  const mask = state.drumColumns[step];
  const isActive = Boolean(mask & bit);
  applyDrumPaint(voice, step, isActive ? "erase" : "fill");
}

function applyDrumPaint(voice, step, mode) {
  if (!Number.isInteger(voice) || voice < 0 || voice >= DRUM_VOICES) {
    return;
  }
  if (!Number.isInteger(step) || step < 0 || step >= STEP_COUNT) {
    return;
  }
  const bit = 1 << voice;
  const mask = state.drumColumns[step];
  const targetActive = mode === "fill";
  const isActive = Boolean(mask & bit);
  if (isActive === targetActive) {
    return;
  }
  const nextMask = targetActive ? mask | bit : mask & ~bit;
  state.drumColumns[step] = nextMask;
  renderDrums();
  sendDrumStepUpdate(voice, step, targetActive);
}

function updateDrumPaintMode(step) {
  if (paintState.startStep == null) {
    return;
  }
  let newMode = paintState.mode;
  if (step > paintState.startStep) {
    newMode = "fill";
  } else if (step < paintState.startStep) {
    newMode = "erase";
  } else if (paintState.mode === null) {
    newMode = paintState.initialMode ?? "fill";
  }
  if (newMode !== paintState.mode) {
    paintState.mode = newMode;
    paintState.appliedStart = false;
    paintState.lastStep = paintState.startStep;
    paintState.visited.clear();
  }
}

function sendDrumStepUpdate(voice, step, isActive) {
  if (!activeOutput) {
    return;
  }

  const payload = [
    SYSEX_START,
    ...MANUFACTURER_ID,
    MSG_SET_DRUM_STEP,
    voice & 0x7f,
    step & 0x7f,
    isActive ? 1 : 0,
    SYSEX_END,
  ];

  try {
    activeOutput.send(payload);
  } catch (error) {
    console.error("Failed to send drum step update:", error);
  }
}

function handleSynthCellPointerDown(event) {
  if (event.button !== 0) {
    return;
  }
  if (event.cancelable) {
    event.preventDefault();
  }
  const cell = event.currentTarget;
  if (!cell) {
    return;
  }
  const voice = Number(cell.dataset.voice);
  const step = Number(cell.dataset.step);
  const mask = state.synthMask[step] ?? 0;
  const isActive = Boolean((mask >> voice) & 0x01);
  const note = computeSynthNoteFromPointer(event, voice, step);
  paintState.synthNote = note;
  paintState.startNote = note;
  startPaint("synth", null, event.pointerId ?? null, BUTTON_PRIMARY);
  paintState.startVoice = voice;
  paintState.startStep = step;
  paintState.startValue = isActive;
  paintState.lastStep = step;
  paintState.appliedStart = false;
}

function handleSynthCellPointerEnter(event) {
  if (!paintState.active || paintState.target !== "synth") {
    return;
  }
  if (paintState.pointerId !== null && event.pointerId !== paintState.pointerId) {
    return;
  }
  if ((event.buttons & paintState.buttonMask) === 0) {
    endPaint();
    return;
  }
  const cell = event.currentTarget;
  const voice = Number(cell.dataset.voice);
  const step = Number(cell.dataset.step);
  if (voice !== paintState.startVoice) {
    return;
  }
  updateSynthPaintMode(step);
  if (paintState.mode === null) {
    return;
  }
  if (!paintState.appliedStart) {
    if (paintState.mode === "fill") {
      applySynthPaint(paintState.startVoice, paintState.startStep, "fill", paintState.startNote);
    } else {
      applySynthPaint(paintState.startVoice, paintState.startStep, "erase");
    }
    paintState.appliedStart = true;
    paintState.lastStep = paintState.startStep;
  }
  if (step === paintState.lastStep) {
    return;
  }
  paintState.lastStep = step;
  if (paintState.mode === "fill") {
    const note = computeSynthNoteFromPointer(event, voice, step);
    paintState.synthNote = note;
    applySynthPaint(voice, step, "fill", note);
  } else {
    applySynthPaint(voice, step, "erase");
  }
}

function handleSynthCellPointerMove(event) {
  if (!paintState.active || paintState.target !== "synth") {
    return;
  }
  if (paintState.pointerId !== null && event.pointerId !== paintState.pointerId) {
    return;
  }
  if ((event.buttons & paintState.buttonMask) === 0) {
    endPaint();
    return;
  }
  if (paintState.mode !== "fill") {
    return;
  }
  const cell = event.currentTarget;
  const voice = Number(cell.dataset.voice);
  const step = Number(cell.dataset.step);
  if (voice !== paintState.startVoice) {
    return;
  }
  const note = computeSynthNoteFromPointer(event, voice, step);
  if (note !== paintState.synthNote || note !== state.synthNotes[voice][step]) {
    paintState.synthNote = note;
    applySynthPaint(voice, step, "fill", note);
  }
}

function handleSynthCellPointerUp(event) {
  if (paintState.target !== "synth") {
    return;
  }
  if (paintState.pointerId !== null && event.pointerId !== paintState.pointerId) {
    return;
  }
  if (paintState.mode === null) {
    const cell = event.currentTarget;
    const voice = Number(cell.dataset.voice);
    const step = Number(cell.dataset.step);
    if (paintState.startValue) {
      applySynthPaint(voice, step, "erase");
    } else {
      applySynthPaint(voice, step, "fill", paintState.startNote ?? paintState.synthNote ?? 0);
    }
  }
  endPaint();
}

function applySynthPaint(voice, step, mode, note) {
  if (!Number.isInteger(voice) || voice < 0 || voice >= SYNTH_VOICES) {
    return;
  }
  if (!Number.isInteger(step) || step < 0 || step >= STEP_COUNT) {
    return;
  }

  const bit = 1 << voice;
  const mask = state.synthMask[step];
  const isActive = Boolean(mask & bit);

  if (mode === "erase") {
    if (!isActive) {
      return;
    }
    state.synthMask[step] = mask & ~bit;
    renderSynths();
    sendSynthStepUpdate(voice, step, false, state.synthNotes[voice][step]);
    return;
  }

  const rawNote = clamp(note ?? state.synthNotes[voice][step] ?? 0, 0, MAX_RAW_NOTE);
  const noteChanged = state.synthNotes[voice][step] !== rawNote;
  if (!noteChanged && isActive) {
    return;
  }

  state.synthMask[step] = mask | bit;
  state.synthNotes[voice][step] = rawNote;
  synthLastNotes[voice] = rawNote;
  renderSynths();
  sendSynthStepUpdate(voice, step, true, rawNote);
}

function computeSynthNoteFromPointer(event, voice, step) {
  const cell = event.currentTarget;
  const rect = cell.getBoundingClientRect();
  const relativeY = clamp((event.clientY - rect.top) / rect.height, 0, 1);
  const inverted = 1 - relativeY;
  let pitchIndex = Math.floor(inverted * PITCH_CLASS_COUNT);
  pitchIndex = clamp(pitchIndex, 0, PITCH_CLASS_COUNT - 1);

  const existingRaw = state.synthNotes[voice][step];
  let baseRaw = Number.isInteger(existingRaw) ? existingRaw : synthLastNotes[voice];
  if (!Number.isInteger(baseRaw)) {
    baseRaw = 0;
  }
  let octave = Math.floor(baseRaw / PITCH_CLASS_COUNT);
  if (octave < 0) {
    octave = 0;
  }
  let rawNote = pitchIndex + octave * PITCH_CLASS_COUNT;
  rawNote = clamp(rawNote, 0, MAX_RAW_NOTE);
  return rawNote;
}

function updateSynthPaintMode(step) {
  if (paintState.startStep == null) {
    return;
  }
  let newMode = paintState.mode;
  if (step > paintState.startStep) {
    newMode = "fill";
  } else if (step < paintState.startStep) {
    newMode = "erase";
  }
  if (newMode !== paintState.mode) {
    paintState.mode = newMode;
    paintState.appliedStart = false;
    paintState.lastStep = paintState.startStep;
  }
}

function sendSynthStepUpdate(voice, step, isActive, rawNote) {
  if (!activeOutput) {
    return;
  }
  const payload = [
    SYSEX_START,
    ...MANUFACTURER_ID,
    MSG_SET_SYNTH_STEP,
    voice & 0x7f,
    step & 0x7f,
    isActive ? 1 : 0,
    rawNote & 0x7f,
    SYSEX_END,
  ];

  try {
    activeOutput.send(payload);
  } catch (error) {
    console.error("Failed to send synth step update:", error);
  }
}

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

async function requestAccess() {
  if (!navigator.requestMIDIAccess) {
    updateStatus("Web MIDI unsupported in this browser.");
    return;
  }
  try {
    if (!midiAccess) {
      midiAccess = await navigator.requestMIDIAccess({ sysex: true });
      midiAccess.onstatechange = handleStateChange;
    }
    attemptAutoConnect();
  } catch (error) {
    console.error(error);
    scheduleRetry("Web MIDI access denied. Retrying…", requestAccess);
  }
}

function handleStateChange() {
  attemptAutoConnect();
}

function attemptAutoConnect() {
  if (!midiAccess) return;
  const inputs = Array.from(midiAccess.inputs.values());
  const outputs = Array.from(midiAccess.outputs.values());
  const blastInput = inputs.find((input) =>
    /blast\s*beats/i.test(`${input.name} ${input.manufacturer || ""}`)
  );
  const blastOutput = outputs.find((output) =>
    /blast\s*beats/i.test(`${output.name} ${output.manufacturer || ""}`)
  );

  if (blastInput) {
    attachInput(blastInput);
  } else {
    detachCurrentInput();
  }

  if (blastOutput) {
    attachOutput(blastOutput);
  } else {
    detachCurrentOutput();
  }

  if (blastInput && blastOutput) {
    clearRetry();
    updateStatus(`Connected: ${blastInput.name || "BlastBeats MIDI"}`);
    maybeRequestSnapshot();
    return;
  }

  const message = !blastInput && !blastOutput
    ? "Searching for BlastBeats MIDI…"
    : !blastInput
      ? "Waiting for BlastBeats MIDI input…"
      : "Waiting for BlastBeats MIDI output…";
  scheduleRetry(message);
}

function attachInput(input) {
  detachCurrentInput();
  activeInput = input;
  activeInput.onmidimessage = handleMIDIMessage;
  updateStatus(`Listening to ${input.name || "MIDI input"}`);
  notifyMidiConnectionChanged();
}

function detachCurrentInput() {
  if (activeInput) {
    activeInput.onmidimessage = null;
    activeInput = null;
    hasRequestedSnapshot = false;
    notifyMidiConnectionChanged();
  }
}

function attachOutput(output) {
  activeOutput = output;
  notifyMidiConnectionChanged();
}

function detachCurrentOutput() {
  if (activeOutput) {
    activeOutput = null;
    hasRequestedSnapshot = false;
    notifyMidiConnectionChanged();
  }
}

function handleMIDIMessage(event) {
  notifyMidiMessage(event);
  const data = event.data;
  if (!data || data.length < 2) return;

  if (data[0] === SYSEX_START && data[data.length - 1] === SYSEX_END) {
    handleSysEx(data);
  }
}

function scheduleRetry(message, action = attemptAutoConnect) {
  updateStatus(message);
  clearRetry();
  retryTimer = setTimeout(() => {
    retryTimer = null;
    action();
  }, RETRY_DELAY_MS);
}

function clearRetry() {
  if (retryTimer) {
    clearTimeout(retryTimer);
    retryTimer = null;
  }
}

function handleSysEx(message) {
  if (!message || message.length < 6) return;

  if (
    message[1] !== MANUFACTURER_ID[0] ||
    message[2] !== MANUFACTURER_ID[1] ||
    message[3] !== MANUFACTURER_ID[2]
  ) {
    return;
  }

  const type = message[4];
  switch (type) {
    case MSG_PATTERN_SNAPSHOT:
      handlePatternSnapshot(message);
      break;
    case MSG_STEP_POSITIONS:
      handleStepPositions(message);
      break;
    default:
      break;
  }
}

function handlePatternSnapshot(message) {
  const requiredLength =
    5 +
    1 +
    10 +
    STEP_COUNT +
    STEP_COUNT +
    SYNTH_VOICES * STEP_COUNT +
    SYNTH_VOICES +
    1;
  if (message.length < requiredLength) {
    return;
  }

  let index = 5;
  index += 1; // pattern number (unused)
  state.selectedVoice = message[index++] & 0x0f;
  state.selectedPage = message[index++] & 0x07;

  for (let i = 0; i < state.pages.length; i++, index++) {
    state.pages[i] = message[index];
  }

  for (let step = 0; step < STEP_COUNT; step++, index++) {
    state.drumColumns[step] = message[index];
  }

  for (let step = 0; step < STEP_COUNT; step++, index++) {
    state.synthMask[step] = message[index];
  }

  for (let voice = 0; voice < SYNTH_VOICES; voice++) {
    for (let step = 0; step < STEP_COUNT; step++, index++) {
      state.synthNotes[voice][step] = message[index];
    }
  }

  for (let voice = 0; voice < SYNTH_VOICES; voice++, index++) {
    state.synthOffsets[voice] = (message[index] ?? OFFSET_BIAS) - OFFSET_BIAS;
  }

  for (let voice = 0; voice < SYNTH_VOICES; voice++) {
    let lastNote = 0;
    for (let step = 0; step < STEP_COUNT; step++) {
      if ((state.synthMask[step] >> voice) & 0x01) {
        lastNote = state.synthNotes[voice][step];
        break;
      }
    }
    synthLastNotes[voice] = lastNote;
  }

  render();
}

function handleStepPositions(message) {
  const requiredLength = 5 + state.currentSteps.length + 1;
  if (message.length < requiredLength) {
    return;
  }

  let index = 5;
  for (let i = 0; i < state.currentSteps.length; i++, index++) {
    state.currentSteps[i] = message[index] & 0x3f;
  }

  flashCurrentSteps();
}

function flashCurrentSteps() {
  for (let voice = 0; voice < DRUM_VOICES; voice++) {
    const step = state.currentSteps[voice] % STEP_COUNT;
    const row = drumCells[voice];
    if (row) {
      flashCell(row[step]);
    }
  }

  for (let voice = 0; voice < SYNTH_VOICES; voice++) {
    const step = state.currentSteps[DRUM_VOICES + voice] % STEP_COUNT;
    const entry = synthCells[voice][step];
    if (entry) {
      flashCell(entry.cell);
    }
  }
}

function flashCell(cell) {
  if (!cell) return;
  cell.classList.remove("is-current-step");
  void cell.offsetWidth; // Force reflow to restart animation.
  cell.classList.add("is-current-step");
  if (cell._flashTimeout) {
    clearTimeout(cell._flashTimeout);
  }
  cell._flashTimeout = setTimeout(() => {
    cell.classList.remove("is-current-step");
    cell._flashTimeout = null;
  }, 200);
}

function render() {
  renderDrums();
  renderSynths();

}

function renderDrums() {
  const selectedDrumVoice =
    state.selectedVoice < DRUM_VOICES ? state.selectedVoice : null;
  const pageStart = state.selectedPage << 4;
  const pageEnd = pageStart + 16;
  const showPageHighlight = selectedDrumVoice !== null;

  for (let step = 0; step < STEP_COUNT; step++) {
    const mask = state.drumColumns[step];
    const inSelectedPage =
      showPageHighlight && step >= pageStart && step < pageEnd;

    for (let voice = 0; voice < DRUM_VOICES; voice++) {
      const cell = drumCells[voice][step];
      if (!cell) continue;
      const isActive = Boolean((mask >> voice) & 0x01);
      const highlightVoice = selectedDrumVoice === voice;

      cell.classList.toggle("active", isActive);
      cell.classList.toggle("is-selected-voice", highlightVoice);
      cell.classList.toggle("is-selected-page", inSelectedPage);
      cell.setAttribute("aria-pressed", isActive ? "true" : "false");
    }
  }
}


function renderSynths() {
  const selectedSynthVoice =
    state.selectedVoice >= DRUM_VOICES
      ? state.selectedVoice - DRUM_VOICES
      : null;
  const pageStart = state.selectedPage << 4;
  const pageEnd = pageStart + 16;

  for (let voice = 0; voice < SYNTH_VOICES; voice++) {
    synthTracks[voice].classList.toggle("is-selected", selectedSynthVoice === voice);

    for (let step = 0; step < STEP_COUNT; step++) {
      const { cell, noteIndicator } = synthCells[voice][step];
      const isActive = (state.synthMask[step] >> voice) & 0x01;
      const rawNote = state.synthNotes[voice][step];
      const offset = state.synthOffsets[voice] ?? 0;
      const pitchClass =
        ((rawNote % PITCH_CLASS_COUNT) + PITCH_CLASS_COUNT) % PITCH_CLASS_COUNT;
      const actualMidi = rawNote + BASE_NOTE + offset * PITCH_CLASS_COUNT;
      const octave =
        Math.floor(actualMidi / PITCH_CLASS_COUNT) - BASE_OCTAVE;
      const inSelectedPage =
        selectedSynthVoice === voice && step >= pageStart && step < pageEnd;

      cell.classList.toggle("active", Boolean(isActive));
      cell.classList.toggle("is-selected-page", inSelectedPage);
      noteIndicator.style.setProperty("--note", pitchClass);

      if (isActive) {
        noteIndicator.dataset.note = String(pitchClass);
        noteIndicator.dataset.octave = octave > 0 ? `+${octave}` : String(octave);
      } else {
        noteIndicator.dataset.note = "";
        noteIndicator.dataset.octave = "";
      }
    }
  }
}


function updateStatus(message) {
  refs.status.textContent = message;
}

function maybeRequestSnapshot() {
  if (!activeInput || !activeOutput) return;
  if (hasRequestedSnapshot) return;
  sendPatternRequest();
  hasRequestedSnapshot = true;
}

function sendPatternRequest() {
  if (!activeOutput) return;
  const payload = [
    SYSEX_START,
    ...MANUFACTURER_ID,
    MSG_PATTERN_REQUEST,
    SYSEX_END,
  ];
  try {
    activeOutput.send(payload);
  } catch (error) {
    console.error("Failed to request pattern snapshot:", error);
    hasRequestedSnapshot = false;
  }
}

init();
